import axios from "axios";
import { getEnvVariable, REACT_APP_API_URL } from "utils/environment";
import { retrieveTokenFromLocalStorage } from "services/utils/serviceutils";
import { HierarchyItemType } from "types/generic";

/* SOC Hierarchy controller */

export const getAllUsersWithPagination = (
    pageSize: number,
    offset: number,
    searchText: string
) => {
    let url = `${getEnvVariable(
        REACT_APP_API_URL
    )}user/?pageSize=${pageSize}&skip=${offset}`;
    if (searchText && searchText !== "") {
        url += `&search=${searchText}`;
    }
    const config: any = {
        method: "get",
        url: url,
        headers: {
            Authorization: `Bearer ${retrieveTokenFromLocalStorage()}`,
        },
    };
    return axios(config);
};

export const getFilteredUserCount = (searchText: string) => {
    let url = `${getEnvVariable(REACT_APP_API_URL)}user/count`;
    if (searchText && searchText !== "") {
        url += `?search=${searchText}`;
    }
    const config: any = {
        method: "get",
        url: url,
        headers: {
            Authorization: `Bearer ${retrieveTokenFromLocalStorage()}`,
        },
    };
    return axios(config);
};

export const getUserByUid = (userUid: string) => {
    const url = `${getEnvVariable(REACT_APP_API_URL)}user/${userUid}`;
    const config: any = {
        method: "get",
        url: url,
        headers: {
            Authorization: `Bearer ${retrieveTokenFromLocalStorage()}`,
        },
    };
    return axios(config);
};

export const getUserPermissionsByUid = (userUid: string) => {
    const url = `${getEnvVariable(
        REACT_APP_API_URL
    )}user/permissions/${userUid}`;
    const config: any = {
        method: "get",
        url: url,
        headers: {
            Authorization: `Bearer ${retrieveTokenFromLocalStorage()}`,
        },
    };
    return axios(config);
};

export const addUserPermissions = (userUid: string, permissions: string[]) => {
    const url = `${getEnvVariable(
        REACT_APP_API_URL
    )}user/permissions/${userUid}`;
    const config: any = {
        method: "post",
        url: url,
        headers: {
            Authorization: `Bearer ${retrieveTokenFromLocalStorage()}`,
        },
        data: permissions,
    };
    return axios(config);
};

export const removeUserPermissions = (
    userUid: string,
    permissions: string[]
) => {
    const url = `${getEnvVariable(
        REACT_APP_API_URL
    )}user/permissions/users.data.length${userUid}`;
    const config: any = {
        method: "delete",
        url: url,
        headers: {
            Authorization: `Bearer ${retrieveTokenFromLocalStorage()}`,
        },
        data: permissions,
    };
    return axios(config);
};

export const createUserDetails = (details: any) => {
    const url = `${getEnvVariable(REACT_APP_API_URL)}user/`;
    const config: any = {
        method: "post",
        url: url,
        headers: {
            Authorization: `Bearer ${retrieveTokenFromLocalStorage()}`,
        },
        data: details,
    };
    return axios(config);
};

export const editUserDetails = (userUid: string, details: any) => {
    const url = `${getEnvVariable(REACT_APP_API_URL)}user/${userUid}`;
    const config: any = {
        method: "put",
        url: url,
        headers: {
            Authorization: `Bearer ${retrieveTokenFromLocalStorage()}`,
        },
        data: details,
    };
    return axios(config);
};

export const addUserRoles = (
    userUid: string,
    provenance: string,
    details: any
) => {
    const url = `${getEnvVariable(REACT_APP_API_URL)}user/roles/${userUid}`;
    const config: any = {
        method: "post",
        url: url,
        params: {
            provenance,
        },
        headers: {
            Authorization: `Bearer ${retrieveTokenFromLocalStorage()}`,
        },
        data: details,
    };
    return axios(config);
};

export const getUserRoles = (userUid: string, provenance: string) => {
    const url = `${getEnvVariable(REACT_APP_API_URL)}user/roles/${userUid}`;
    const config: any = {
        method: "get",
        url: url,
        params: {
            provenance,
        },
        headers: {
            Authorization: `Bearer ${retrieveTokenFromLocalStorage()}`,
        },
    };
    return axios(config);
};

export const removeUserRoles = (
    userUid: string,
    provenance: string,
    details: any
) => {
    const url = `${getEnvVariable(REACT_APP_API_URL)}user/roles/${userUid}`;
    const config: any = {
        method: "delete",
        url: url,
        params: {
            provenance,
        },
        headers: {
            Authorization: `Bearer ${retrieveTokenFromLocalStorage()}`,
        },
        data: details,
    };
    return axios(config);
};

/* SOC Hierarchy controller */

/* PORTAL groups and roles controller */

export const getRoleGroups = (provenance: string) => {
    const url = `${getEnvVariable(REACT_APP_API_URL)}roleGroup/`;
    const config: any = {
        method: "get",
        url: url,
        headers: {
            Authorization: `Bearer ${retrieveTokenFromLocalStorage()}`,
        },
        params: { provenance: provenance },
    };
    return axios(config);
};

export const getRolesByGroupId = (groupId: number) => {
    const url = `${getEnvVariable(
        REACT_APP_API_URL
    )}roleGroup/roles/${groupId}`;
    const config: any = {
        method: "get",
        url: url,
        headers: {
            Authorization: `Bearer ${retrieveTokenFromLocalStorage()}`,
        },
    };
    return axios(config);
};

export const getRolesByProvenance = (provenance: string) => {
    const url = `${getEnvVariable(
        REACT_APP_API_URL
    )}roleGroup/roles/provenance?provenance=${provenance}`;
    const config: any = {
        method: "get",
        url: url,
        headers: {
            Authorization: `Bearer ${retrieveTokenFromLocalStorage()}`,
        },
    };
    return axios(config);
};

export const createRoleGroup = (
    provenance: string,
    name: string,
    body: { name: string }[]
) => {
    const url = `${getEnvVariable(
        REACT_APP_API_URL
    )}roleGroup/${provenance}/${name}`;
    const config: any = {
        method: "post",
        url: url,
        headers: {
            Authorization: `Bearer ${retrieveTokenFromLocalStorage()}`,
        },
        data: body,
    };
    return axios(config);
};

export const getUserGroups = (provenance: string, userUid: string) => {
    const url = `${getEnvVariable(REACT_APP_API_URL)}roleGroup/user`;
    const config: any = {
        method: "get",
        url: url,
        headers: {
            Authorization: `Bearer ${retrieveTokenFromLocalStorage()}`,
        },
        params: { provenance: provenance, userUid: userUid },
    };
    return axios(config);
};

export const addUserToGroup = (groupId: number, userUid: string) => {
    const url = `${getEnvVariable(REACT_APP_API_URL)}roleGroup/user`;
    const config: any = {
        method: "put",
        url: url,
        headers: {
            Authorization: `Bearer ${retrieveTokenFromLocalStorage()}`,
        },
        params: { groupId: groupId, userUid: userUid },
    };
    return axios(config);
};

export const removeUserToGroup = (groupId: number, userUid: string) => {
    const url = `${getEnvVariable(REACT_APP_API_URL)}roleGroup/user`;
    const config: any = {
        method: "delete",
        url: url,
        headers: {
            Authorization: `Bearer ${retrieveTokenFromLocalStorage()}`,
        },
        params: { groupId: groupId, userUid: userUid },
    };
    return axios(config);
};

/* PORTAL groups and roles controller */

export const getUserPreferences = () => {
    const url = `${getEnvVariable(
        REACT_APP_API_URL
    )}userConfigurationPreferences/`;
    const config: any = {
        method: "get",
        url: url,
        headers: {
            Authorization: `Bearer ${retrieveTokenFromLocalStorage()}`,
        },
    };
    return axios(config);
};

export const postUserPreferences = (
    dateFormat: string,
    language: string,
    timeFormat: string,
    weekStartDay: string
) => {
    const url = `${getEnvVariable(
        REACT_APP_API_URL
    )}userConfigurationPreferences/`;
    const config: any = {
        method: "post",
        url: url,
        data: {
            dateFormat: dateFormat,
            language: language,
            timeFormat: timeFormat,
            weekStartDay: weekStartDay,
        },
        headers: {
            Authorization: `Bearer ${retrieveTokenFromLocalStorage()}`,
        },
    };
    return axios(config);
};

export const getUnitPreferences = () => {
    const url = `${getEnvVariable(REACT_APP_API_URL)}unitConfigurationPrefs/`;
    const config: any = {
        method: "get",
        url: url,
        headers: {
            Authorization: `Bearer ${retrieveTokenFromLocalStorage()}`,
        },
    };
    return axios(config);
};
